/**
 * EmojiPickerButton
 *
 * Dos modos de uso:
 *  A) Flotante (admin/simpleforms): sin `onOpenChange` → panel flotante clásico (sin cambios).
 *  B) Controlado (chats): con `onOpenChange` → solo renderiza el botón trigger;
 *     el padre controla la apertura y renderiza <EmojiBottomPanel> debajo del input.
 *
 * Props nuevas:
 *   - `onOpenChange(isOpen)` → activa modo controlado.
 *   - `isOpen` → opcional, para sincronizar el icono del botón en modo controlado.
 */
import { useState, useRef, useEffect, useLayoutEffect, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import { Smile, Loader2 } from "lucide-react";

const EmojiPickerReact = lazy(() => import("emoji-picker-react"));

const PICKER_WIDTH  = 340;
const PICKER_HEIGHT = 400;
const SKIN_TONE_KEY = "startip_emoji_skin_tone";

const SIZE_CLS = {
  sm: "w-7 h-7",
  md: "w-8 h-8",
};
const ICON_CLS = {
  sm: "w-4 h-4",
  md: "w-5 h-5",
};

// Categorías de emojis traducidas al español
const CATEGORIES_ES = [
  { category: "suggested",      name: "Usados recientemente" },
  { category: "smileys_people", name: "Emojis y personas"    },
  { category: "animals_nature", name: "Animales y naturaleza" },
  { category: "food_drink",     name: "Comida y bebida"      },
  { category: "travel_places",  name: "Viajes y lugares"     },
  { category: "activities",     name: "Actividades"          },
  { category: "objects",        name: "Objetos"              },
  { category: "symbols",        name: "Símbolos"             },
  { category: "flags",          name: "Banderas"             },
];

// ── Tono de piel persistido en localStorage ───────────────────────────────────
export function getSavedSkinTone() {
  try { return localStorage.getItem(SKIN_TONE_KEY) || undefined; } catch { return undefined; }
}
export function saveSkinTone(tone) {
  try { localStorage.setItem(SKIN_TONE_KEY, tone); } catch {}
}

export { CATEGORIES_ES };

// ── Panel de emojis de ancho completo (modo chat / bottom-sheet) ──────────────
// Layout WhatsApp: sin buscador, categorías al pie, 7 filas de emojis visibles.
//
// Estructura real del DOM de emoji-picker-react v4:
//   aside.EmojiPickerReact  (display:flex; flex-direction:column — asignado por la lib)
//     div.epr-header
//       div.epr-search-container  ← buscador a ocultar
//       nav.epr-category-nav      ← barra de categorías que queremos abajo
//     div.epr-body                ← grid de emojis
//
// Truco: aplicar `display:contents` al .epr-header hace que sus hijos pasen
// a ser flex-items directos del aside raíz. Luego usamos `order` para
// colocar .epr-category-nav DESPUÉS de .epr-body.
const BOTTOM_PANEL_CSS = `
  /* Quitar bordes del panel */
  .epr-bs .EmojiPickerReact {
    border: none !important;
    border-radius: 0 !important;
  }

  /* Header "transparente" en flex — sus hijos pasan al flujo del root */
  .epr-bs .EmojiPickerReact .epr-header {
    display: contents !important;
  }

  /* Ocultar únicamente el buscador (que está dentro del header) */
  .epr-bs .EmojiPickerReact .epr-search-container {
    display: none !important;
  }

  /* Barra de categorías al fondo: order 2 y borde arriba */
  .epr-bs .EmojiPickerReact .epr-category-nav {
    order: 2 !important;
    border-top: 1px solid #27272a !important;
    border-bottom: none !important;
    padding: 5px 4px !important;
    flex-shrink: 0 !important;
  }

  /* Grid de emojis arriba, ocupa el espacio sobrante, máx 7 filas ≈ 270px */
  .epr-bs .EmojiPickerReact .epr-body {
    order: 1 !important;
    flex: 1 !important;
    max-height: 270px !important;
    overflow-y: auto !important;
  }
`;

export function EmojiBottomPanel({ onSelect }) {
  return (
    <div className="epr-bs w-full border-t border-zinc-800 bg-zinc-950 animate-in slide-in-from-bottom-2 duration-200">
      {/* Inyección de estilos layout WhatsApp — aislados por la clase .epr-bs */}
      <style>{BOTTOM_PANEL_CSS}</style>

      {/* Handle visual */}
      <div className="flex items-center justify-center pt-2 pb-0.5">
        <div className="w-10 h-1 rounded-full bg-zinc-700" />
      </div>

      <Suspense
        fallback={
          <div className="flex items-center justify-center" style={{ height: 320 }}>
            <Loader2 className="w-5 h-5 text-neon-pink animate-spin" />
          </div>
        }
      >
        <EmojiPickerReact
          onEmojiClick={(data) => onSelect?.(data.emoji)}
          onSkinToneChange={saveSkinTone}
          defaultSkinTone={getSavedSkinTone()}
          searchDisabled
          skinTonesDisabled
          theme="dark"
          categories={CATEGORIES_ES}
          width="100%"
          height={325}
          lazyLoadEmojis
          previewConfig={{ showPreview: false }}
          autoFocusSearch={false}
        />
      </Suspense>
    </div>
  );
}

export default function EmojiPickerButton({
  onSelect,
  onOpenChange,        // si se pasa → modo controlado (no renderiza panel flotante)
  isOpen: isOpenProp,  // opcional — sincroniza icono en modo controlado
  size = "md",
  align = "end",
  className = "",
  disabled = false,
  testId = "emoji-picker-button",
}) {
  const controlled = typeof onOpenChange === "function";
  const [openInternal, setOpenInternal] = useState(false);
  const open = controlled ? !!isOpenProp : openInternal;

  const containerRef  = useRef(null);
  // Posición fija calculada para NO salirse del viewport (mobile y desktop)
  const [pickerPos, setPickerPos] = useState(null);

  // Cerrar al hacer clic fuera (solo modo flotante)
  useEffect(() => {
    if (controlled || !openInternal) return;
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpenInternal(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [controlled, openInternal]);

  // Calcular posición óptima del picker para no salir del viewport
  useLayoutEffect(() => {
    if (controlled || !openInternal || !containerRef.current) {
      setPickerPos(null);
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const vw   = window.innerWidth;
    const vh   = window.innerHeight;
    const PAD  = 8;

    // En móvil estrecho: ocupar casi todo el ancho seguro (left+right, sin width fija)
    // Usamos left+right en vez de left+width para escapar cualquier transform de ancestros
    if (vw < 600) {
      const bottom = Math.min(vh - rect.top + PAD, vh - PICKER_HEIGHT - PAD);
      setPickerPos({ left: PAD, right: PAD, bottom: Math.max(bottom, PAD) });
      return;
    }

    // Desktop: picker de ancho fijo (340px), posición inteligente
    let left = rect.right - PICKER_WIDTH;   // alineado a la derecha del botón por defecto
    if (left < PAD) left = rect.left;       // si se va a la izquierda, alinear a la izquierda
    if (left + PICKER_WIDTH > vw - PAD) left = vw - PICKER_WIDTH - PAD; // borde derecho

    // Arriba del botón si cabe, si no, debajo
    const top = rect.top - PICKER_HEIGHT - PAD;
    const bottom = top < PAD ? rect.bottom + PAD : null;
    const finalTop = top < PAD ? rect.bottom + PAD : top;

    setPickerPos({ left, top: finalTop, width: PICKER_WIDTH });
  }, [controlled, openInternal]);

  const handleToggle = () => {
    if (controlled) {
      onOpenChange(!isOpenProp);
    } else {
      setOpenInternal((v) => !v);
    }
  };

  return (
    <div ref={containerRef} className="relative inline-block shrink-0">
      <button
        type="button"
        disabled={disabled}
        aria-label={open ? "Cerrar selector de emojis" : "Abrir selector de emojis"}
        aria-expanded={open}
        data-testid={testId}
        onClick={handleToggle}
        className={`${SIZE_CLS[size]} flex items-center justify-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-neon-pink/40 disabled:opacity-40 disabled:cursor-not-allowed
          ${open
            ? "text-neon-pink bg-neon-pink/10"
            : "text-zinc-400 hover:text-neon-pink hover:bg-neon-pink/10"}
          ${className}`}
      >
        <Smile className={ICON_CLS[size]} />
      </button>

      {/* Panel flotante — renderizado via Portal para escapar transforms de ancestros (motion.div, etc.)
          Esto garantiza que position:fixed se calcula siempre relativo al viewport, sin importar
          el stacking context del contenedor padre. */}
      {!controlled && openInternal && pickerPos && createPortal(
        <div
          data-testid={`${testId}-panel`}
          className="fixed z-[9999] rounded-xl overflow-hidden shadow-2xl border border-white/10"
          style={{
            left:  pickerPos.left  ?? 'auto',
            right: pickerPos.right ?? 'auto',
            width: pickerPos.width ?? 'auto',
            // mobile → bottom; desktop → top
            ...(pickerPos.bottom != null
              ? { bottom: pickerPos.bottom }
              : { top:    pickerPos.top }),
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <Suspense
            fallback={
              <div
                className="flex items-center justify-center bg-zinc-950"
                style={{ width: pickerPos.width ?? (pickerPos.right != null ? undefined : 350), height: PICKER_HEIGHT }}
              >
                <Loader2 className="w-5 h-5 text-neon-pink animate-spin" />
              </div>
            }
          >
            <EmojiPickerReact
              onEmojiClick={(data) => onSelect?.(data.emoji)}
              onSkinToneChange={saveSkinTone}
              defaultSkinTone={getSavedSkinTone()}
              theme="dark"
              categories={CATEGORIES_ES}
              width={pickerPos.width ?? "100%"}
              height={PICKER_HEIGHT}
              lazyLoadEmojis
              searchPlaceholder="Buscar emoji..."
              previewConfig={{ showPreview: false }}
              autoFocusSearch={false}
            />
          </Suspense>
        </div>,
        document.body
      )}
    </div>
  );
}

/**
 * Helper para insertar `emoji` en la posición actual del cursor de un
 * input/textarea controlado por React. Devuelve el nuevo string que el
 * padre debe pasar a `setValue`. Si se proporciona `maxCodepoints` y se
 * superaría, devuelve null.
 */
export function insertEmojiAtCursor(refEl, currentValue, emoji, maxCodepoints = Infinity) {
  let start = currentValue.length;
  let end = currentValue.length;
  if (refEl && typeof refEl.selectionStart === "number") {
    start = refEl.selectionStart;
    end = refEl.selectionEnd;
  }
  const next = currentValue.slice(0, start) + emoji + currentValue.slice(end);
  if ([...next].length > maxCodepoints) return null;
  requestAnimationFrame(() => {
    if (refEl) {
      const newPos = start + emoji.length;
      try {
        refEl.focus();
        refEl.setSelectionRange(newPos, newPos);
      } catch { /* ignore */ }
    }
  });
  return next;
}


